import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Wallet } from 'lucide-react';
import axios from 'axios';
import { useCourseApi } from '../../hooks/useCourseApi';
import { useAuth } from '../../contexts/AuthContext';
import { getTenantHeaders } from '../../config/tenant';
import { walletService } from '../../services/walletService';
import { formatCoins } from '../../utils/walletLabels';

interface ContactInfo {
  id: number;
  whatsApp_Number: string;
  facebook_Page: string;
  youTube_Channel: string;
  tiktokChannel: string;
}

interface ContactApiResponse {
  success: boolean;
  message: string;
  data: ContactInfo[];
}

type Step = 'choose' | 'wallet' | 'code' | 'success';

interface EnrollmentPopupProps {
  isOpen: boolean;
  onClose: () => void;
  courseId: string;
  courseName?: string;
  coursePrice?: number;
  onPurchased?: () => void;
}

const EnrollmentPopup: React.FC<EnrollmentPopupProps> = ({
  isOpen,
  onClose,
  courseId,
  courseName,
  coursePrice = 0,
  onPurchased,
}) => {
  const [step, setStep] = useState<Step>('choose');
  const [code, setCode] = useState('');
  const [promoCode, setPromoCode] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [whatsappNumber, setWhatsappNumber] = useState('');
  const [balance, setBalance] = useState<number | null>(null);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [coinsSpent, setCoinsSpent] = useState(0);
  const [newBalance, setNewBalance] = useState(0);

  const navigate = useNavigate();
  const { enrollInCourse } = useCourseApi();
  const { isAuthenticated } = useAuth();

  const priceInCoins = Math.round(Number(coursePrice) || 0);
  const canAfford = balance != null && balance >= priceInCoins;

  useEffect(() => {
    const fetchWhatsAppNumber = async () => {
      try {
        const response = await axios.get<ContactApiResponse>('https://api.ibrahim-magdy.com/api/Contact/getAll', {
          headers: {
            ...getTenantHeaders()
          }
        });
        if (response.data.success && response.data.data.length > 0) {
          setWhatsappNumber(response.data.data[0].whatsApp_Number);
        }
      } catch (error) {
        console.error('Error fetching WhatsApp number:', error);
      }
    };

    if (isOpen) {
      fetchWhatsAppNumber();
      setIsClosing(false);
      setError('');
      setCode('');
      setPromoCode('');
      setStep('choose');
      setCoinsSpent(0);
      setNewBalance(0);
      setBalance(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || step !== 'wallet' || !isAuthenticated) return;

    let cancelled = false;
    const loadBalance = async () => {
      setBalanceLoading(true);
      const result = await walletService.getBalance();
      if (cancelled) return;
      if (result.success) {
        setBalance(result.balance);
        setError('');
      } else {
        setBalance(null);
        setError(result.error || 'تعذر تحميل رصيد المحفظة');
      }
      setBalanceLoading(false);
    };

    loadBalance();
    return () => {
      cancelled = true;
    };
  }, [isOpen, step, isAuthenticated]);

  const handleWhatsAppContact = () => {
    if (whatsappNumber) {
      const text = courseName
        ? `مرحباً، أريد الاستفسار عن التسجيل في كورس ${courseName}`
        : 'مرحباً، أريد الاستفسار عن التسجيل في الدورة';
      window.open(`https://wa.me/${whatsappNumber}?text=${encodeURIComponent(text)}`, '_blank');
    }
  };

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setStep('choose');
      setError('');
      setCode('');
      setPromoCode('');
      setIsClosing(false);
    }, 200);
  };

  const openWalletStep = () => {
    if (!isAuthenticated) {
      navigate('/login', { state: { redirectTo: `/courses/${courseId}` } });
      return;
    }
    setError('');
    setStep('wallet');
  };

  const handleWalletPurchase = async () => {
    if (!isAuthenticated) {
      navigate('/login', { state: { redirectTo: `/courses/${courseId}` } });
      return;
    }

    try {
      setIsLoading(true);
      setError('');
      const result = await walletService.buyCourse(courseId, promoCode || undefined);
      if (result.success) {
        setCoinsSpent(result.data.coinsSpent);
        setNewBalance(result.data.newBalance);
        setStep('success');
        onPurchased?.();
      } else {
        setError(result.error);
        if (result.error.includes('رصيدك غير كافٍ')) {
          const balanceResult = await walletService.getBalance();
          if (balanceResult.success) setBalance(balanceResult.balance);
        }
      }
    } catch {
      setError('حدث خطأ أثناء الشراء من المحفظة');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCodeSubmit = async () => {
    if (!code.trim()) {
      setError('يرجى إدخال كود التسجيل');
      return;
    }

    if (!isAuthenticated) {
      setError('يرجى تسجيل الدخول للتسجيل في هذه الدورة');
      return;
    }

    try {
      setIsLoading(true);
      setError('');
      const response = await enrollInCourse(courseId, code);
      
      if (response.success && response.message !== 'Code not found.') {
        setStep('success');
        onPurchased?.();
      } else {
        setError(
          response.message === 'Code not found.'
            ? 'الكود غير موجود'
            : response.message || 'كود التسجيل غير صالح'
        );
      }
    } catch {
      setError('حدث خطأ أثناء التسجيل في الدورة');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoToCourse = () => {
    navigate(`/course-player/${courseId}`);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 ${
        isClosing ? 'animate-fadeOut' : 'animate-fadeIn'
      }`}
    >
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={handleClose} />
      <div
        className={`relative w-full max-w-lg transform rounded-2xl border-2 border-teal-100 bg-white shadow-2xl transition-all duration-300 ${
          isClosing ? 'scale-95 opacity-0' : 'scale-100 opacity-100'
        }`}
      >
        <div className="p-6 sm:p-8" dir="rtl">
          {step === 'choose' && (
            <div className="space-y-6">
              <div className="text-center">
                <h2 className="mb-2 text-2xl font-extrabold text-slate-900">
                  اختر طريقة التسجيل
                </h2>
                <p className="text-slate-600">
                  {courseName ? `التسجيل في: ${courseName}` : 'حدد كيف تريد التسجيل في هذه الدورة'}
                </p>
                {priceInCoins > 0 && (
                  <p className="mt-2 text-sm font-semibold text-teal-600">
                    السعر: {formatCoins(priceInCoins)} عملة
                  </p>
                )}
              </div>

              <div className="space-y-3">
                {priceInCoins > 0 && (
                  <button
                    type="button"
                    onClick={openWalletStep}
                    className="group relative flex w-full items-center justify-center gap-3 rounded-xl bg-teal-600 px-4 py-3 text-white shadow-md shadow-teal-700/20 transition hover:bg-teal-700"
                  >
                    <Wallet className="h-5 w-5" />
                    الشراء من المحفظة
                  </button>
                )}

                <button
                  type="button"
                  onClick={() => {
                    setError('');
                    setStep('code');
                  }}
                  className="flex w-full items-center justify-center gap-3 rounded-xl border-2 border-teal-100 bg-white px-4 py-3 font-semibold text-slate-900 transition hover:bg-teal-50"
                >
                  لدي كود تسجيل
                </button>

                <button
                  type="button"
                  onClick={handleWhatsAppContact}
                  className="flex w-full items-center justify-center gap-3 rounded-xl bg-green-600 px-4 py-3 font-semibold text-white transition hover:bg-green-700"
                >
                  التواصل مع الإشراف
                </button>
              </div>

              <button
                type="button"
                onClick={handleClose}
                className="w-full py-2 text-slate-500 transition hover:text-slate-700"
              >
                إلغاء
              </button>
            </div>
          )}

          {step === 'wallet' && (
            <div className="space-y-5">
              <div className="text-center">
                <h2 className="mb-2 text-2xl font-extrabold text-slate-900">
                  الشراء من المحفظة
                </h2>
                <p className="text-sm text-slate-600">1 عملة = 1 جنيه مصري</p>
              </div>

              <div className="space-y-3 rounded-xl border-2 border-teal-100 bg-white p-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">سعر الكورس</span>
                  <span className="font-bold text-slate-900">
                    {formatCoins(priceInCoins)} عملة
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">رصيدك الحالي</span>
                  <span className="font-bold text-teal-600">
                    {balanceLoading
                      ? '...'
                      : balance != null
                        ? `${formatCoins(balance)} عملة`
                        : '—'}
                  </span>
                </div>
              </div>

              <div>
                <label htmlFor="promoCode" className="mb-1.5 block text-sm font-semibold text-slate-900">
                  كود خصم (اختياري)
                </label>
                <input
                  id="promoCode"
                  type="text"
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value)}
                  placeholder="أدخل كود الخصم إن وجد"
                  className="w-full rounded-xl border-2 border-teal-100 bg-white px-4 py-3 text-sm outline-none focus:border-teal-500"
                  disabled={isLoading}
                />
              </div>

              {error && (
                <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700">
                  {error}
                  {error.includes('رصيدك غير كافٍ') && (
                    <Link
                      to="/student-portal/wallet"
                      className="mt-2 block font-semibold text-teal-600 hover:text-teal-700"
                      onClick={handleClose}
                    >
                      اشحن المحفظة ←
                    </Link>
                  )}
                </div>
              )}

              {!balanceLoading && balance != null && !canAfford && !error && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-900">
                  رصيدك غير كافٍ. تحتاج {formatCoins(priceInCoins - balance)} عملة إضافية.
                  <Link
                    to="/student-portal/wallet"
                    className="mt-2 block font-semibold text-teal-600 hover:text-teal-700"
                    onClick={handleClose}
                  >
                    اشحن المحفظة ←
                  </Link>
                </div>
              )}

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={handleWalletPurchase}
                  disabled={isLoading || balanceLoading || (balance != null && !canAfford)}
                  className="flex-1 rounded-xl bg-teal-600 py-3 font-semibold text-white shadow-md shadow-teal-700/20 transition hover:bg-teal-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isLoading ? 'جاري الشراء...' : 'تأكيد الشراء'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setStep('choose');
                    setError('');
                    setPromoCode('');
                  }}
                  disabled={isLoading}
                  className="flex-1 rounded-xl border-2 border-teal-100 bg-white py-3 font-semibold text-slate-900 hover:bg-teal-50"
                >
                  رجوع
                </button>
              </div>
            </div>
          )}

          {step === 'code' && (
            <div className="space-y-6">
              <div className="text-center">
                <h2 className="mb-2 text-2xl font-extrabold text-slate-900">
                  أدخل الكود
                </h2>
                <p className="text-slate-600">يرجى إدخال كود التسجيل أدناه</p>
              </div>
              <div className="space-y-4">
                <input
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !isLoading) handleCodeSubmit();
                  }}
                  placeholder="أدخل كود التسجيل"
                  className={`w-full rounded-xl border-2 px-4 py-3 outline-none transition ${
                    error
                      ? 'border-red-300 bg-red-50'
                      : 'border-teal-100 bg-white focus:border-teal-500'
                  }`}
                  disabled={isLoading}
                />
                {error && <p className="text-sm text-red-600">{error}</p>}
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={handleCodeSubmit}
                    disabled={isLoading}
                    className="flex-1 rounded-xl bg-teal-600 py-3 font-semibold text-white hover:bg-teal-700 disabled:opacity-50"
                  >
                    {isLoading ? 'جاري التحقق...' : 'تحقق من الكود'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setStep('choose');
                      setError('');
                      setCode('');
                    }}
                    className="flex-1 rounded-xl border-2 border-teal-100 bg-white py-3 font-semibold text-slate-900 hover:bg-teal-50"
                    disabled={isLoading}
                  >
                    رجوع
                  </button>
                </div>
              </div>
            </div>
          )}

          {step === 'success' && (
            <div className="space-y-6">
              <div className="text-center">
                <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-teal-50 text-teal-600">
                  <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h2 className="mb-2 text-2xl font-extrabold text-slate-900">
                  تم التسجيل بنجاح!
                </h2>
                <p className="text-slate-600">يمكنك الآن بدء التعلم</p>
                {coinsSpent > 0 && (
                  <p className="mt-2 text-sm text-teal-600">
                    تم خصم {formatCoins(coinsSpent)} عملة — رصيدك الآن {formatCoins(newBalance)}
                  </p>
                )}
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={handleGoToCourse}
                  className="flex-1 rounded-xl bg-teal-600 py-3 font-semibold text-white hover:bg-teal-700"
                >
                  بدء الدورة
                </button>
                <button
                  type="button"
                  onClick={() => {
                    handleClose();
                    navigate('/student-portal');
                  }}
                  className="flex-1 rounded-xl border-2 border-teal-100 bg-white py-3 font-semibold text-slate-900 hover:bg-teal-50"
                >
                  كورساتي
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EnrollmentPopup; 